import { Skeleton } from "@/components/ui/skeleton";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import { api, type ContainerUsageByDay, type UsageReportPeriod } from "@/lib/api";
import { formatCostLong, formatMoney } from "@/lib/format";
import { purposeLabel } from "@/lib/usage-purpose";
import { cn } from "@/lib/utils";
import {
  buildLedgerChart,
  buildScaledChart,
  bucketKeys,
  seriesTotal,
  type ChartRow,
  type Grain,
  type Segment,
  type StackedChart,
} from "@/lib/spend-chart-data";
import { reportRetry, useActiveFilters, useSpendAccess } from "@/lib/use-spend-report";
import type { LedgerSplit } from "@/pages/container-tabs/SpendAttribution";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useMemo, type ReactNode } from "react";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";

// The Usage tab's spend chart. Stacked by "Doing what" whenever the Boo's own
// usage report is available to this viewer; otherwise (report off for this
// Boo or viewer, report failing, spend-only viewers) one ledger series.
//
// Every bar is reconciled to the credit ledger (see lib/spend-chart-data.ts):
// - 7d / 30d / All time, unfiltered: each day's (month's) bar IS the ledger's
//   total for it. Model charges are split by what the Boo was doing, by the
//   brain's proportions for that bucket; infrastructure, add-on services and
//   base fee are the ledger's own figures.
// - Last 24 hours: the ledger is daily, so bars are hourly model usage, the
//   window's model charges split across hours and purposes by the brain's
//   proportions; infrastructure and add-ons are stated in the note instead.
// - Filtered to a person, channel or conversation: the ledger has no such
//   dimension, so bars are the brain's filtered figures scaled by the same
//   window ratio the breakdown cards use, model usage only.
// - Filtered to a purpose: only that purpose's segment, computed as above.

const HOUR_LABEL: Intl.DateTimeFormatOptions = { hour: "numeric" };

function bucketDate(bucket: string, grain: Grain): Date {
  if (grain === "hour") return new Date(`${bucket}:00:00Z`);
  const [y, m, d] = bucket.split("-").map(Number);
  // Day and month keys are calendar labels: shown as the same date locally.
  return new Date(y, m - 1, grain === "month" ? 1 : d);
}

function tickLabel(bucket: string, grain: Grain): string {
  const date = bucketDate(bucket, grain);
  if (grain === "hour") return date.toLocaleTimeString(undefined, HOUR_LABEL);
  if (grain === "month") return date.toLocaleDateString(undefined, { month: "short", year: "numeric" });
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function tooltipLabel(bucket: string, grain: Grain): string {
  const date = bucketDate(bucket, grain);
  if (grain === "hour") {
    return date.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  }
  return tickLabel(bucket, grain);
}

export function SpendChart({
  containerId,
  period,
  ledgerOnly,
  days,
  bounds,
  ledger,
}: {
  containerId: string;
  period: UsageReportPeriod;
  ledgerOnly: boolean;
  days: ContainerUsageByDay["days"];
  /** The ledger read's bounds; null for All time (monthly buckets). */
  bounds: { start: Date; end: Date } | null;
  ledger: LedgerSplit;
}) {
  const access = useSpendAccess(containerId, !ledgerOnly);
  const navigate = useNavigate();

  // The same URL filters the breakdown cards read, passed through verbatim
  // (including the "__none__" scopes for work with no person or channel).
  const { filter } = useActiveFilters();
  const { purpose, ...scopeFilter } = filter;
  const scoped = Object.keys(scopeFilter).length > 0;

  // Split by purpose and never filtered by it: a purpose's share of each
  // bucket needs every purpose in that bucket, and toggling the purpose
  // filter then needs no refetch.
  const series = useQuery({
    queryKey: ["usage-series", containerId, period, "purpose", scopeFilter],
    queryFn: () =>
      api.containers.usage.report.series(containerId, period, { ...scopeFilter, splitBy: "purpose" }),
    enabled: access === "on",
    retry: reportRetry,
    placeholderData: keepPreviousData,
  });
  // The brain's unfiltered window total: the denominator of the window ratio
  // the cards use for filtered views (shares the breakdown's cache entry).
  const windowTotals = useQuery({
    queryKey: ["usage-report-totals", containerId, period],
    queryFn: () => api.containers.usage.report.totals(containerId, period),
    enabled: access === "on" && scoped,
    retry: reportRetry,
    placeholderData: keepPreviousData,
  });

  const grain: Grain = period === "24h" ? "hour" : period === "all" ? "month" : "day";
  const stacked = useMemo((): StackedChart | null => {
    const points = series.data?.points;
    if (points === undefined) return null;
    if (scoped) {
      const brainWindow = windowTotals.data?.totals.costUsd;
      if (brainWindow === undefined) return null;
      // No usable denominator: figures pass through unscaled, as on the cards.
      const scale = ledger.ai > 0 && brainWindow > 0 ? ledger.ai / brainWindow : 1;
      return buildScaledChart({
        grain,
        window: bounds,
        points,
        target: seriesTotal(points) * scale,
        selected: purpose,
      });
    }
    if (grain === "hour") {
      // Nothing to proportion the window's model charges by: the ledger
      // chart says it better.
      if (ledger.ai !== 0 && seriesTotal(points) <= 0) return null;
      return buildScaledChart({ grain, window: bounds, points, target: ledger.ai, selected: purpose });
    }
    return buildLedgerChart({ grain, window: bounds, days, points, selected: purpose });
  }, [series.data, windowTotals.data, scoped, grain, bounds, ledger.ai, days, purpose]);

  // Toggle the purpose filter exactly as a "Doing what" card row does: same
  // URL param, and the change PUSHES history so Back undoes it.
  const togglePurpose = (next: string) =>
    void navigate({
      to: ".",
      search: (prev: Record<string, unknown>) => {
        const out: Record<string, unknown> = { ...prev };
        if (out.uPurpose === next) delete out.uPurpose;
        else out.uPurpose = next;
        return out;
      },
    });

  const pending =
    access === "pending" ||
    (access === "on" &&
      !series.isError &&
      !windowTotals.isError &&
      (series.isLoading || (scoped && windowTotals.isLoading)));
  const title =
    grain === "hour" && stacked !== null
      ? "Hourly spend"
      : grain === "month"
        ? "Monthly spend"
        : "Daily spend";

  let body;
  if (pending) {
    body = <Skeleton className="mt-4 h-64 w-full" />;
  } else if (stacked === null || access !== "on" || series.isError || (scoped && windowTotals.isError)) {
    body = (
      <>
        <LedgerChart days={days} grain={grain === "month" ? "month" : "day"} bounds={bounds} />
        <p className="mt-2 text-xs text-muted-foreground">
          Charges land when they're deducted from your balance — model usage
          as it happens, infrastructure with each sync. Org-wide charges appear
          as Shared charges on your organization's Usage page.
        </p>
      </>
    );
  } else {
    body = (
      <>
        <div className={cn(series.isFetching && "opacity-60 transition-opacity")}>
          <StackedSpendChart chart={stacked} selected={purpose} onToggle={togglePurpose} />
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          {purpose !== undefined
            ? `Showing ${purposeLabel(purpose)} only. Click it again to show everything.`
            : scoped
              ? "Model usage for the filters above. Infrastructure and add-ons aren't tied to a person, channel or conversation, so they're left out."
              : grain === "hour"
                ? `Hourly bars are model usage: ${formatCostLong(ledger.ai)} of the ${formatCostLong(ledger.total)} Total spend. Infrastructure and add-ons make up the rest; they're charged daily, not by the hour.`
                : `Each bar is everything deducted from your balance that ${grain === "month" ? "month" : "day"}: model usage split by what your Boo was doing, plus infrastructure and add-ons. Org-wide charges appear as Shared charges on your organization's Usage page.`}
        </p>
      </>
    );
  }

  return (
    <section className="rounded-lg border p-4">
      <h2 className="text-base font-semibold">{title}</h2>
      {body}
    </section>
  );
}

// ── Single series: the ledger's totals ─────────────────────────────────────

// The ledger's bars in the first categorical slot (see --viz-* in index.css)
// rather than the near-black --chart-5: one series, one friendly color.
const ledgerConfig: ChartConfig = {
  total: { label: "Spend", color: "var(--viz-1)" },
};

function LedgerChart({
  days,
  grain,
  bounds,
}: {
  days: ContainerUsageByDay["days"];
  grain: "day" | "month";
  bounds: { start: Date; end: Date } | null;
}) {
  const rows = useMemo(() => {
    const byKey = new Map(days.map((d) => [d.day, d.total]));
    return bucketKeys(grain, bounds, byKey.keys()).map((k) => ({ bucket: k, total: byKey.get(k) ?? 0 }));
  }, [days, grain, bounds]);
  return (
    <ChartContainer config={ledgerConfig} className="mt-4 h-64 w-full">
      <BarChart data={rows} margin={{ left: 8, right: 8 }}>
        <CartesianGrid vertical={false} />
        <XAxis
          dataKey="bucket"
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          minTickGap={24}
          tickFormatter={(v: string) => tickLabel(v, grain)}
        />
        <YAxis tickLine={false} axisLine={false} width={56} tickFormatter={(v: number) => formatMoney(v)} />
        <ChartTooltip content={<ChartTooltipContent valueFormatter={(v) => formatCostLong(Number(v))} />} />
        {/* Fill straight from config — the ChartStyle --color-* vars
            don't resolve reliably in this setup. */}
        <Bar dataKey="total" fill={ledgerConfig.total.color} maxBarSize={96} />
      </BarChart>
    </ChartContainer>
  );
}

// ── Stacked by "Doing what" ─────────────────────────────────────────────────

function StackedSpendChart({
  chart,
  selected,
  onToggle,
}: {
  chart: StackedChart;
  selected: string | undefined;
  onToggle: (purpose: string) => void;
}) {
  const { grain, rows, segments } = chart;
  const config = useMemo(
    () =>
      Object.fromEntries(segments.map((s) => [s.id, { label: s.label, color: s.color }])) as ChartConfig,
    [segments],
  );
  return (
    <>
      <ChartContainer config={config} className="mt-4 h-64 w-full">
        <BarChart data={rows} margin={{ left: 8, right: 8 }}>
          <CartesianGrid vertical={false} />
          <XAxis
            dataKey="bucket"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            minTickGap={24}
            tickFormatter={(v: string) => tickLabel(v, grain)}
          />
          <YAxis tickLine={false} axisLine={false} width={56} tickFormatter={(v: number) => formatMoney(v)} />
          <ChartTooltip
            content={({ active, payload }) => {
              const row = payload?.[0]?.payload as ChartRow | undefined;
              if (!active || row === undefined) return null;
              return <StackTooltip row={row} segments={segments} grain={grain} />;
            }}
          />
          {segments.map((s) => (
            <Bar
              key={s.id}
              dataKey={s.id}
              stackId="spend"
              fill={s.color}
              maxBarSize={96}
              // The surface-colored stroke is the gap between stacked segments.
              stroke="var(--background)"
              strokeWidth={1.5}
              className={s.purpose !== undefined ? "cursor-pointer" : undefined}
              onClick={s.purpose !== undefined ? () => onToggle(s.purpose!) : undefined}
            />
          ))}
        </BarChart>
      </ChartContainer>
      <Legend segments={segments} selected={selected} onToggle={onToggle} />
    </>
  );
}

// Below the chart, in stack order. Purpose items are the same toggle as the
// segments; "Other activity" and the ledger's charges are labels only.
function Legend({
  segments,
  selected,
  onToggle,
}: {
  segments: readonly Segment[];
  selected: string | undefined;
  onToggle: (purpose: string) => void;
}) {
  if (segments.length === 0) return null;
  return (
    <ul className="mt-3 flex flex-wrap items-center gap-x-1 gap-y-1 text-xs">
      {segments.map((s) => {
        const swatch = <span className="size-2.5 shrink-0 rounded-[2px]" style={{ backgroundColor: s.color }} />;
        return (
          <li key={s.id}>
            {s.purpose !== undefined ? (
              <button
                type="button"
                onClick={() => onToggle(s.purpose!)}
                aria-pressed={selected === s.purpose}
                title={selected === s.purpose ? "Clear this filter" : "Filter to this"}
                className={cn(
                  "flex items-center gap-1.5 rounded px-1.5 py-1 transition-colors",
                  selected === s.purpose ? "bg-muted" : "hover:bg-muted/50",
                )}
              >
                {swatch}
                {s.label}
              </button>
            ) : (
              <span className="flex items-center gap-1.5 px-1.5 py-1 text-muted-foreground">
                {swatch}
                {s.label}
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function StackTooltip({
  row,
  segments,
  grain,
}: {
  row: ChartRow;
  segments: readonly Segment[];
  grain: Grain;
}) {
  // Top of the stack first, as the eye reads the bar.
  const shown = segments.filter((s) => typeof row[s.id] === "number" && row[s.id] !== 0).reverse();
  return (
    <TooltipFrame label={tooltipLabel(row.bucket, grain)}>
      {shown.map((s) => (
        <TooltipLine key={s.id} color={s.color} label={s.label} value={row[s.id] as number} />
      ))}
      {shown.length !== 1 && (
        <div className="mt-0.5 flex items-center justify-between gap-4 border-t pt-1.5 font-medium">
          <span>Total</span>
          <span className="font-mono tabular-nums">{formatCostLong(row.total)}</span>
        </div>
      )}
    </TooltipFrame>
  );
}

function TooltipFrame({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid min-w-44 gap-1.5 rounded-lg border border-border/50 bg-background px-2.5 py-1.5 text-xs shadow-xl">
      <div className="font-medium">{label}</div>
      {children}
    </div>
  );
}

function TooltipLine({ color, label, value }: { color: string; label: string; value: number }) {
  return (
    <div className="flex items-center gap-2">
      <span className="size-2.5 shrink-0 rounded-[2px]" style={{ backgroundColor: color }} />
      <span className="flex-1 text-muted-foreground">{label}</span>
      <span className="font-mono font-medium text-foreground tabular-nums">{formatCostLong(value)}</span>
    </div>
  );
}
